import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PagePanel, Button, ButtonSize, EditIcon, Box } from 'edenred-ui';
import { useFormContext } from 'react-hook-form';
import { useTheme } from 'styled-components';

import { registeredVoucherOrderAction } from '@epi-store/registeredVoucherOrder/registeredVoucherOrder.slice';
import { getOrderData } from '@epi-store/registeredVoucherOrder/registeredVoucherOrder.selectors';
import { FormFields } from '@epi-forms/helpers';
import { getFinnishAddresses } from '@epi-selectors/api';
import { DeliveryPickerProps } from '@epi-models/forms/DeliveryPicker';

import { DeliveryMethods } from '../../constants/deliveryMethods';
import { LoadingContainer } from '../../components/LoadingContainer/LoadingContainer';
import { RoundButton } from '../../components/RoundButton/RoundButton';
import * as navigationActions from '../../actions/navigation';

import './DeliveryPicker.scss';

function DeliveryPicker({
  editPage,
  goToPage,
  isLoading,
  companyAdress
}: DeliveryPickerProps) {
  const { t } = useTranslation();
  const { gray50, sizeMedium } = useTheme();
  const dispatch = useDispatch();
  const addresses = useSelector(getFinnishAddresses);
  const deliveryAddress = useSelector(getOrderData);
  const { setValue } = useFormContext();
  const isSavedAddressChosen =
    (deliveryAddress.deliveryMethod === DeliveryMethods.ToCompanyAddress ||
      deliveryAddress.deliveryMethod === DeliveryMethods.ToSavedAddress) &&
    addresses?.find(address => address.id === deliveryAddress.addressId);

  useEffect(() => {
    if (!deliveryAddress?.addressId && companyAdress?.id) {
      dispatch(
        registeredVoucherOrderAction.addOrderInformation({
          addressId: companyAdress?.id,
          deliveryCompanyName: companyAdress?.companyName,
          deliveryAddress: companyAdress?.addressLine,
          deliveryZipCode: companyAdress?.postCode,
          deliveryCity: companyAdress?.city
        })
      );
    }
    setValue(FormFields.DeliveryMethod, DeliveryMethods.ToCompanyAddress);
    setValue(FormFields.AddressId, companyAdress?.id);
  }, [companyAdress?.addressLine]);

  const deliveryDetails = () => {
    if (isSavedAddressChosen) {
      return addresses?.find(
        address => address.id === deliveryAddress.addressId
      )?.label;
    }
    if (deliveryAddress.deliveryMethod === DeliveryMethods.ToPickupPoint) {
      return (
        <span>
          <a
            key="link"
            href={t(
              'forms.delivery_info.delivery_method.nearest_pickup_point.link_url'
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t(
              'forms.delivery_info.delivery_method.nearest_pickup_point.link_text'
            )}
          </a>
        </span>
      );
    }
    return `${
      deliveryAddress.deliveryCompanyName
        ? deliveryAddress.deliveryCompanyName + ','
        : ''
    } 
        ${deliveryAddress.deliveryAddress}, 
        ${deliveryAddress.deliveryZipCode}, 
        ${deliveryAddress.deliveryCity}`;
  };

  return (
    <div className="DeliveryPicker">
      <PagePanel
        title={t('containers.voucher_order.delivery_info_header')}
        className="px-10"
      >
        <Box pl={3} color={gray50} fontSize={sizeMedium}>
          {editPage && (
            <span className="visible-xs-inline">
              <RoundButton
                id="delivery-picker-edit-btn"
                icon={<EditIcon />}
                className="pull-right"
                onClick={() => goToPage(editPage)}
              />
            </span>
          )}
          <LoadingContainer isLoading={!!isLoading}>
            <div>
              {deliveryAddress.deliveryMethod === DeliveryMethods.ToPickupPoint
                ? t('containers.order_details.to_pickup_point')
                : t('components.delivery.delivery_to_street')}
              <br />
              {deliveryDetails()}
            </div>
            {deliveryAddress.contactPersons ? (
              <div>
                {`${t('components.delivery.contact_person')} ${
                  deliveryAddress.contactPersons
                }`}
              </div>
            ) : null}
            {editPage && (
              <Button
                id="delivery-picker-edit-btn"
                className="btn-edit pull-right"
                onClick={() => goToPage(editPage)}
                size={ButtonSize.Medium}
              >
                {t('components.benefit_summary.edit')}
              </Button>
            )}
          </LoadingContainer>
        </Box>
      </PagePanel>
    </div>
  );
}

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...navigationActions }, dispatch);

const DeliveryPickerWithI18n = connect(
  null,
  mapDispatchToProps
)(DeliveryPicker);

export { DeliveryPickerWithI18n as DeliveryPicker };
